import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui/label';
import { Calendar as CalendarIcon, Clock } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
interface TimingPickerProps {
  timing: string;
  openCalendar: boolean;
  setOpenCalendar: (open: boolean) => void;
  selectedDate: Date | undefined;
  handleSelectDate: (date: Date | undefined) => void;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}
const TimingPicker: React.FC<TimingPickerProps> = ({
  timing,
  openCalendar,
  setOpenCalendar,
  selectedDate,
  handleSelectDate,
  handleInputChange
}) => {
  const {
    t
  } = useTranslation();
  const [openUrgency, setOpenUrgency] = useState(false);
  const [urgencyValue, setUrgencyValue] = useState('');
  const [dateValue, setDateValue] = useState('');
  const urgencyOptions = [{
    value: 'immediate',
    label: t('requestForm.urgencyImmediate')
  }, {
    value: 'nextTwoDays',
    label: t('requestForm.urgencyNextTwoDays')
  }, {
    value: 'nextWeek',
    label: t('requestForm.urgencyNextWeek')
  }, {
    value: 'upToMonth',
    label: t('requestForm.urgencyUpToMonth')
  }, {
    value: 'monthOrMore',
    label: t('requestForm.urgencyMonthOrMore')
  }];

  // Initialize values from timing prop
  React.useEffect(() => {
    if (timing) {
      // Check if it's a date format (contains /)
      if (timing.includes('/')) {
        setDateValue(timing);
        setUrgencyValue('');
      } else {
        // It's an urgency key - find the matching option label for display
        const matchingOption = urgencyOptions.find(opt => opt.value === timing);
        setUrgencyValue(matchingOption?.label || timing); // Fallback to timing if not found (for backward compatibility)
        setDateValue('');
      }
    }
  }, [timing, urgencyOptions]);
  const handleUrgencySelect = (value: string, label: string) => {
    setUrgencyValue(label); // Show translated label in UI
    setDateValue('');
    setOpenUrgency(false);

    // Store the key (not the translated label) for consistent database storage
    const syntheticEvent = {
      target: {
        name: 'timing',
        value: value // Store key like "immediate", "nextWeek", etc.
      }
    } as React.ChangeEvent<HTMLInputElement>;
    handleInputChange(syntheticEvent);
  };
  const handleDateSelect = (date: Date | undefined) => {
    handleSelectDate(date);
    setUrgencyValue('');
    if (date) {
      const formattedDate = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
      setDateValue(formattedDate);
    }
  };
  return <div className="space-y-2">
      <Label className="text-foreground">{t('requestForm.timingOptional')}</Label>

      <div className="space-y-3">
        {/* Urgency Select */}
        <div>
          <Label htmlFor="urgency" className="text-sm text-muted-foreground mb-1.5 block">{t('requestForm.timeRange')}</Label>
          <Popover open={openUrgency} onOpenChange={setOpenUrgency}>
            <PopoverTrigger asChild>
              <div className="relative cursor-pointer">
                <Clock className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
                <Input id="urgency" placeholder={t('requestForm.urgencyPlaceholder')} value={urgencyValue} readOnly className="pr-10 rounded-lg cursor-pointer bg-background" />
              </div>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 z-50 bg-background border-border" align="start" side="bottom" avoidCollisions={false}>
              <div className="p-3 bg-background">
                <div className="flex flex-col gap-1">
                  {urgencyOptions.map(option => <Button key={option.value} variant="ghost" className={cn("justify-start text-right hover:bg-accent", urgencyValue === option.label && "bg-accent")} onClick={() => handleUrgencySelect(option.value, option.label)}>
                      <Clock className="ml-2 h-4 w-4" />
                      {option.label}
                    </Button>)}
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* OR divider */}
        <div className="flex items-center gap-2">
          <div className="flex-1 h-px bg-border" />
          <span className="text-muted-foreground text-lg font-bold text-yellow-600">{t('common.or')}</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        {/* Date Select */}
        <div>
          <Label htmlFor="date" className="text-sm text-muted-foreground mb-1.5 block">{t('requestForm.workDate')}</Label>
          <Popover open={openCalendar} onOpenChange={setOpenCalendar}>
            <PopoverTrigger asChild>
              <div className="relative cursor-pointer">
                <CalendarIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
                <Input id="date" placeholder="dd/mm/yyyy" value={dateValue} readOnly className="pr-10 rounded-lg cursor-pointer bg-background" />
              </div>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 z-50 bg-background border-border" align="start" side="bottom" avoidCollisions={false}>
              <div className="p-3 bg-background">
                <CalendarComponent mode="single" selected={selectedDate} onSelect={handleDateSelect} className="rounded-md pointer-events-auto bg-background" disabled={date => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                return date < today;
              }} initialFocus />
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>;
};
export default TimingPicker;